import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { AddOrEditIntegrationDrawerFooter } from '@modules/integrationsV2/components/AddOrEditIntegrationDrawerFooter';
import { EditIntegrationIncompleteWarningAlert } from '@modules/integrationsV2/components/EditIntegrationDrawerContent/EditIntegrationIncompleteWarningAlert';
import { IntegrationDescriptionForm } from '@modules/integrationsV2/components/IntegrationDescriptionForm';
import {
  useUpdateIntegration,
  useValidateIntegrationForm,
} from '@modules/integrationsV2/hooks';
import { EditIntegrationDrawerContentProps } from '@modules/integrationsV2/types';
import { trackEditIntegrationEvent } from '@modules/integrationsV2/utils';
import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material';
import {
  FacebookPage,
  IntegrationChannelType,
  UpdateIntegrationMutationVariables,
  useGetIntegrationEditFormDataQuery,
} from 'generated/graphql';
import { SyntheticEvent, useState } from 'react';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { AddOrEditIntegrationDrawerHeader } from '../../AddOrEditIntegrationDrawerHeader';

export const EditFacebookPagesIntegrationDrawerContent = ({
  onDrawerClose,
  integrationID,
  integrationData,
  integrationType,
  description,
  integrationChannel,
}: EditIntegrationDrawerContentProps) => {
  const { showErrorNotification } = useNotificationContext();

  const [selectedPage, setSelectedPage] = useState<FacebookPage | undefined>();
  const [descriptionInput, setDescriptionInput] = useState(description);

  const existingPage = integrationData.facebookPages?.subscribedPage;

  const { data: editFormData, loading: isLoadingFormData } =
    useGetIntegrationEditFormDataQuery({
      variables: {
        integrationID,
      },
      skip: !!existingPage,
      fetchPolicy: 'cache-and-network',
      onError: () => {
        showErrorNotification({
          contentComponent: 'Failed to fetch integrations form data.',
          props: {},
        });
      },
    });

  const handleChange = (_: SyntheticEvent, value: FacebookPage | null) => {
    if (!value) {
      return;
    }
    setSelectedPage(value);
  };

  const availablePages =
    editFormData?.getIntegrationEditFormData?.formData?.facebookPages?.pages ??
    [];

  const [updateIntegration, { status: updateIntegrationStatus }] =
    useUpdateIntegration({
      channelType: IntegrationChannelType.FacebookPages,
      integrationType,
      onCompleted: () => {
        onDrawerClose();
        trackEditIntegrationEvent({
          eventName: 'Edit Integration Submitted',
          channelType: IntegrationChannelType.FacebookPages,
          integrationID,
        });
      },
    });

  const [
    submitForm,
    {
      isDisabled,
      isDescriptionRequired,
      isValidatingDescription,
      descriptionValidForQuestions,
      validatedDescriptionMessage,
      isPreValidatedDescription,
    },
  ] = useValidateIntegrationForm({
    channelType: IntegrationChannelType.FacebookPages,
    integrationType,
    hasValidFormFields: !existingPage ? !!selectedPage : true,
    descriptionInput,
    currentDescription: description,
    onValidated: async () => {
      let integrationPayload: UpdateIntegrationMutationVariables | null = null;

      if (!existingPage && selectedPage) {
        integrationPayload = {
          integrationID,
          isActive: true,
          updateData: {
            facebookPages: {
              subscribedPage: {
                id: selectedPage.id,
                name: selectedPage.name,
                token: selectedPage.token ?? '',
              },
            },
          },
          updateDescription: {
            description: descriptionInput.trim(),
          },
        };
      } else {
        integrationPayload = {
          integrationID,
          isActive: true,
          updateDescription: {
            description: descriptionInput.trim(),
          },
        };
      }

      updateIntegration(integrationPayload);
    },
  });

  const isSubmitting = IsLoading(updateIntegrationStatus);

  return (
    <Stack height="100%" justifyContent="space-between">
      <Box>
        <AddOrEditIntegrationDrawerHeader
          integrationChannel={integrationChannel}
          integrationType={integrationType}
          title="Edit Facebook Pages Integration"
        />
        <Box p={3}>
          <EditIntegrationIncompleteWarningAlert
            integrationData={integrationData}
            channelType={IntegrationChannelType.FacebookPages}
          />
          <Stack
            spacing={4}
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              submitForm();
            }}
            autoComplete="off"
          >
            {!existingPage && (
              <Stack spacing={2}>
                <Stack>
                  <Typography variant="subtitle1">
                    Select Facebook Page
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Select the page you would like Enterpret to get data from
                  </Typography>
                </Stack>
                <Autocomplete
                  fullWidth
                  value={selectedPage}
                  options={availablePages}
                  getOptionLabel={(option) => option.name}
                  onChange={handleChange}
                  renderInput={(params) => (
                    <TextField {...params} label="Facebook Page" />
                  )}
                  componentsProps={{ paper: { elevation: 8 } }}
                  loading={isLoadingFormData}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value?.id
                  }
                />
              </Stack>
            )}
            <IntegrationDescriptionForm
              value={descriptionInput}
              onChange={setDescriptionInput}
              isRequired={isDescriptionRequired}
              validatedDescriptionMessage={validatedDescriptionMessage}
              isValidationInProgress={isValidatingDescription}
              descriptionValidForQuestions={descriptionValidForQuestions}
              isPreValidatedDescription={isPreValidatedDescription}
              integrationType={integrationType}
            />
          </Stack>
        </Box>
      </Box>
      <AddOrEditIntegrationDrawerFooter
        onCancel={onDrawerClose}
        onSubmit={submitForm}
        isDisabled={isDisabled}
        isLoading={isSubmitting}
      />
    </Stack>
  );
};
